import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Select,
  Alert
} from '../../styles/components';
import { SearchableSelect } from '../common/SearchableSelect';
import { SecretDisplayModal } from '../common/SecretDisplayModal';
import { 
  useCreateClientMutation,
  useSetClientEndpointMutation,
  useSetClientUsernameMutation,
  useSetClientCustomerIdMutation,
  useSetClientPasswordMutation,
  useSetCertificateMutation,
  useSetClientApiKeyMutation,
  useSetClientKeyPairMutation,
  useActivateClientMutation
} from '../../store/api/clientsApi';
import { useGetProvidersQuery } from '../../store/api/providersApi';
import { useGetOrganizationsQuery } from '../../store/api/organizationsApi';
import { useGetUserProfileQuery } from '../../store/api/userApi';
import { useNotifications } from '../../hooks/useNotifications';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { updateUserProfile } from '../../store/slices/authSlice';
import { CreateClientRequest } from '../../types/client';
import { Provider, AuthType } from '../../types/api';
import { 
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Import step components
import { CredentialsStep } from './wizard-steps/CredentialsStep';
import { ApiKeyStep } from './wizard-steps/ApiKeyStep';
import { CustomerIdStep } from './wizard-steps/CustomerIdStep';
import { KeypairStep } from './wizard-steps/KeypairStep';
import { CertificateStep } from './wizard-steps/CertificateStep';

interface CreateClientWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ClientData {
  // Step 1: Basic Info
  clientId: string;     // Auto-generated client ID returned from backend
  organizationId: string;
  organizationName: string;
  
  // Step 2: Financial Institution Setup
  providerId: string;
  providerName: string;
  authType: AuthType | null;
  
  // Credentials (vary by auth type)
  username: string;
  password: string;
  apiKey: string;
  customerId: string;
  certificate: File | null;
  certificatePassword: string;
  keypairGenerated: boolean;
}

const StepIndicator = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const Step = styled.div<{ $active: boolean; $completed: boolean; $error: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  font-weight: ${props => props.theme.fontWeights.medium};
  font-size: ${props => props.theme.fontSizes.sm};
  flex-shrink: 0;
  
  ${props => {
    if (props.$error) {
      return `
        background-color: ${props.theme.colors.error[500]};
        color: white;
      `;
    } else if (props.$completed) {
      return `
        background-color: ${props.theme.colors.success[500]};
        color: white;
      `;
    } else if (props.$active) {
      return `
        background-color: ${props.theme.colors.primary[500]};
        color: white;
      `;
    } else {
      return `
        background-color: ${props.theme.colors.gray[200]};
        color: ${props.theme.colors.gray[500]};
      `;
    }
  }}
`;

const StepLabel = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.2;
  word-wrap: break-word;
  hyphens: auto;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

// Helper function to get institution type label
const getInstitutionTypeLabel = (authType: AuthType | undefined | null): string => {
  if (authType === null || authType === undefined) return '';
  
  switch (authType) {
    case AuthType.UsernamePasswordCertificate:
      return 'Bank';
    case AuthType.UsernamePassword:
      return 'Payment Processor';
    case AuthType.ApiKeyKeypair:
      return 'Card Processor';
    case AuthType.CustomerIdSignature:
      return 'Bank';
    default:
      return 'Financial Institution';
  }
};

// Helper function to get steps based on auth type
const getStepsForAuthType = (authType: AuthType | null, useSystemCertificate: boolean = false) => {
  const baseSteps = [
    { key: 'basic', label: 'Organization' },
    { key: 'provider', label: 'Institution' }
  ];

  if (authType === null || authType === undefined) {
    return [...baseSteps, { key: 'review', label: 'Review' }];
  }

  switch (authType) {
    case AuthType.UsernamePasswordCertificate:
      return [
        ...baseSteps,
        { key: 'credentials', label: 'Credentials' },
        { key: 'certificate', label: 'Certificate' },
        { key: 'review', label: 'Review & Activate' }
      ];
    case AuthType.UsernamePassword:
      return [
        ...baseSteps,
        { key: 'credentials', label: 'Credentials' },
        { key: 'review', label: 'Review & Activate' }
      ];
    case AuthType.ApiKeyKeypair:
      return [
        ...baseSteps,
        { key: 'apikey', label: 'API Key' },
        { key: 'keypair', label: 'Key Pair' },
        { key: 'review', label: 'Review & Activate' }
      ];
    case AuthType.CustomerIdSignature:
      return [
        ...baseSteps,
        { key: 'customerId', label: 'Customer ID' },
        { key: 'review', label: 'Review & Activate' }
      ];
    default:
      return [...baseSteps, { key: 'review', label: 'Review' }];
  }
};

export function CreateClientWizard({ onClose, onSuccess }: CreateClientWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [errorStep, setErrorStep] = useState<number | null>(null);
  const [clientData, setClientData] = useState<ClientData>({
    clientId: '',
    organizationId: '',
    organizationName: '',
    providerId: '',
    providerName: '',
    authType: null,
    username: '',
    password: '',
    apiKey: '',
    customerId: '',
    certificate: null,
    certificatePassword: '',
    keypairGenerated: false
  });
  const [createdClientId, setCreatedClientId] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState<string>('');
  const [showSecretModal, setShowSecretModal] = useState(false);

  const dispatch = useDispatch();
  const { notify } = useNotifications();
  const { data: providers } = useGetProvidersQuery();
  const { data: organizations } = useGetOrganizationsQuery({
    page: 1,
    pageSize: 9999
  });
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [setEndpoint, { isLoading: isSettingEndpoint }] = useSetClientEndpointMutation();
  const [setUsername, { isLoading: isSettingUsername }] = useSetClientUsernameMutation();
  const [setPassword, { isLoading: isSettingPassword }] = useSetClientPasswordMutation();
  const [setCustomerId] = useSetClientCustomerIdMutation();
  const [setCertificate, { isLoading: isSettingCertificate }] = useSetCertificateMutation();
  const [setApiKey, { isLoading: isSettingApiKey }] = useSetClientApiKeyMutation();
  const [setKeyPair, { isLoading: isSettingKeyPair }] = useSetClientKeyPairMutation();
  const [activateClient, { isLoading: isActivating }] = useActivateClientMutation();

  const isLoading = isCreating || isSettingEndpoint || isSettingUsername || 
                   isSettingPassword || isSettingCertificate || isActivating ||
                   isSettingApiKey || isSettingKeyPair;

  // Get the selected organization to check useSystemCertificate
  const selectedOrganization = organizations?.items?.find(org => 
    org.organizationId === clientData.organizationId
  );

  // Get dynamic steps based on selected provider's auth type and organization certificate settings
  const steps = getStepsForAuthType(clientData.authType, selectedOrganization?.useSystemCertificate || false);

  // Get user profile data for regular users (contains organization info)
  const { data: userProfile } = useGetUserProfileQuery(undefined, {
    skip: !user || user?.scope === 'support',
  });

  // Initialize organization data for regular users
  useEffect(() => {
    if (user && user.scope !== 'support' && userProfile) {
      updateClientData({
        organizationId: userProfile.organizationId,
        organizationName: userProfile.name
      });
    }
  }, [user, userProfile]);

  const updateClientData = (updates: Partial<ClientData>) => {
    setClientData(prev => ({ ...prev, ...updates }));
  };

  const handleProviderSelect = (provider: Provider) => {
    updateClientData({
      providerId: provider.id.toString(),
      providerName: provider.name,
      authType: provider.authType !== undefined ? provider.authType : null
    });
  };

  const handleFileUpload = (file: File) => {
    updateClientData({ certificate: file });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const executeStep = async (stepIndex: number): Promise<boolean> => {
    try {
      setErrorStep(null);
      const stepKey = steps[stepIndex].key;
      
      switch (stepKey) {
        case 'basic':
          const createRequest: CreateClientRequest = {
            name: 'New Client',
            generatesecret: true
          };
          if (user?.scope === 'support') {
            createRequest.organizationId = clientData.organizationId;
          }
          const result = await createClient(createRequest).unwrap();
          updateClientData({ clientId: result.id });
          setCreatedClientId(result.id);
          
          // Update hasClients in Redux so onboarding disappears immediately
          dispatch(updateUserProfile({ hasClients: true }));
          
          // If secret is returned, store it for display after completion
          if (result.secret) {
            setGeneratedSecret(result.secret);
          }
          break;
          
        case 'provider':
          await setEndpoint({
            clientId: clientData.clientId,
            endpointName: clientData.providerName
          }).unwrap();
          break;
          
        case 'credentials':
          await setUsername({
            clientId: clientData.clientId,
            username: clientData.username
          }).unwrap();
          await setPassword({
            clientId: clientData.clientId,
            password: clientData.password
          }).unwrap();
          break;

        case 'customerId':
          await setCustomerId({
            clientId: clientData.clientId,
            customerId: clientData.customerId
          }).unwrap();
          break;

        case 'apikey':
          await setApiKey({
            clientId: clientData.clientId,
            apiKey: clientData.apiKey
          }).unwrap();
          break;
          
        case 'keypair':
          await setKeyPair(clientData.clientId).unwrap();
          updateClientData({ keypairGenerated: true });
          break;
          
        case 'certificate':
          if (clientData.certificate) {
            await setCertificate({
              clientId: clientData.clientId,
              certificate: clientData.certificate,
              password: clientData.certificatePassword
            }).unwrap();
          }
          break;
          
        case 'review':
          await activateClient(clientData.clientId).unwrap();
          break;
      }
      
      setCompletedSteps(prev => [...prev, stepIndex]);
      return true;
    } catch (error: any) {
      setErrorStep(stepIndex);
      notify.error(
        `Step ${stepIndex + 1} failed`, 
        error?.data?.message || 'Please check your input and try again.'
      );
      return false;
    }
  };

  const handleNext = async () => {
    const success = await executeStep(currentStep);
    if (success) {
      if (currentStep === steps.length - 1) {
        if (generatedSecret) {
          setShowSecretModal(true);
        } else {
          notify.success('Client created successfully', 'The client has been created and activated.');
          onSuccess();
        }
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleRetry = () => {
    setErrorStep(null);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErrorStep(null);
    }
  };

  const handleSecretComplete = () => {
    setShowSecretModal(false);
    setGeneratedSecret('');
    notify.success('Client created successfully', 'The client has been created and activated.');
    onSuccess();
  };

  const canProceed = () => {
    const stepKey = steps[currentStep].key;
    
    switch (stepKey) {
      case 'basic':
        if (user?.scope === 'support') {
          return clientData.organizationId;
        } else {
          return userProfile?.organizationId;
        }
      case 'provider':
        return !!clientData.providerId;  // Only check if provider is selected, authType might be null
      case 'credentials':
        return clientData.username && clientData.password;
      case 'customerId':
        return clientData.customerId;
      case 'apikey':
        return clientData.apiKey;
      case 'keypair':
        return true; // Auto-generates
      case 'certificate':
        return selectedOrganization?.useSystemCertificate || clientData.certificate;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    const stepKey = steps[currentStep].key;
    
    switch (stepKey) {
      case 'basic':
        return (
          <>
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
              <p style={{ margin: 0, color: '#374151', fontSize: '0.875rem' }}>
                A unique Client ID will be automatically generated when you create the client.
              </p>
            </div>
            {user?.scope === 'support' ? (
              <FormGroup>
                <Label htmlFor="organization">Organization *</Label>
                <SearchableSelect
                  id="organization"
                  value={clientData.organizationId}
                  onChange={(value) => {
                    const selectedOrg = organizations?.items?.find(org => org.organizationId === value);
                    updateClientData({
                      organizationId: value,
                      organizationName: selectedOrg?.name || ''
                    });
                  }}
                  options={organizations?.items?.map(org => ({
                    value: org.organizationId,
                    label: `${org.name} (${org.organizationId})`
                  })) || []}
                  placeholder="Search for an organization..."
                />
                {selectedOrganization?.useSystemCertificate && (
                  <div style={{ marginTop: '0.75rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
                    <p style={{ margin: 0, color: '#374151', fontSize: '0.875rem' }}>
                      The Organization uses system certificate
                    </p>
                  </div>
                )}
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Organization (Auto-selected)</Label>
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  color: '#374151'
                }}>
                  <div style={{ fontWeight: '500' }}>
                    {userProfile?.name || 'Loading organization...'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Client ID: {userProfile?.clientId || 'Loading...'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Organization ID: {userProfile?.organizationId || 'Loading...'}
                  </div>
                </div>
                <small style={{ color: '#6b7280', marginTop: '0.5rem', display: 'block' }}>
                  This client will be created under your organization
                </small>
              </FormGroup>
            )}
          </>
        );
        
      case 'provider':
        return (
          <>
            <FormGroup>
              <Label htmlFor="provider">Financial Institution *</Label>
              <Select
                id="provider"
                value={clientData.providerId}
                onChange={(e) => {
                  const selectedProvider = providers?.find(p => p.id.toString() === e.target.value);
                  if (selectedProvider) {
                    handleProviderSelect(selectedProvider);
                  }
                }}
              >
                <option value="">Select a financial institution</option>
                {providers?.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </Select>
              <small style={{ color: '#6b7280', marginTop: '0.5rem', display: 'block' }}>
                Select the financial institution for this client connection
              </small>
            </FormGroup>
          </>
        );
        
      case 'credentials':
        return (
          <CredentialsStep
            username={clientData.username}
            password={clientData.password}
            onUsernameChange={(value) => updateClientData({ username: value })}
            onPasswordChange={(value) => updateClientData({ password: value })}
            providerName={clientData.providerName}
          />
        );

      case 'customerId':
        return (
          <CustomerIdStep
            customerId={clientData.customerId}
            onCustomerIdChange={(value) => updateClientData({ customerId: value })}
            providerName={clientData.providerName}
          />
        );

      case 'apikey':
        return (
          <ApiKeyStep
            apiKey={clientData.apiKey}
            onApiKeyChange={(value) => updateClientData({ apiKey: value })}
            providerName={clientData.providerName}
          />
        );
        
      case 'keypair':
        return (
          <KeypairStep
            clientId={clientData.clientId}
            providerName={clientData.providerName}
            isGenerating={isSettingKeyPair}
            onGenerate={() => {}}
            isGenerated={clientData.keypairGenerated}
          />
        );
        
      case 'certificate':
        return (
          <CertificateStep
            certificate={clientData.certificate}
            certificatePassword={clientData.certificatePassword}
            onCertificateChange={handleFileUpload}
            onPasswordChange={(value) => updateClientData({ certificatePassword: value })}
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileError={(message) => notify.error('File Error', message)}
            providerName={clientData.providerName}
            useSystemCertificate={selectedOrganization?.useSystemCertificate}
          />
        );
        
      case 'review':
        const typeLabel = getInstitutionTypeLabel(clientData.authType);
        return (
          <div>
            <h3 style={{ marginBottom: '1rem' }}>Review Configuration</h3>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem' }}>
              <p><strong>Client ID:</strong> {clientData.clientId}</p>
              <p><strong>Organization:</strong> {clientData.organizationName}</p>
              <p><strong>Financial Institution:</strong> {clientData.providerName}
                {typeLabel && <span style={{ color: '#6b7280', fontSize: '0.875rem' }}> ({typeLabel})</span>}
              </p>
              
              {clientData.authType === AuthType.UsernamePasswordCertificate && (
                <>
                  <p><strong>Username:</strong> {clientData.username}</p>
                  {!selectedOrganization?.useSystemCertificate && (
                    <p><strong>Certificate:</strong> {clientData.certificate?.name}</p>
                  )}
                  {selectedOrganization?.useSystemCertificate && (
                    <p><strong>Certificate:</strong> Using system certificate</p>
                  )}
                </>
              )}
              
              {clientData.authType === AuthType.UsernamePassword && (
                <>
                  <p><strong>Username:</strong> {clientData.username}</p>
                </>
              )}

              {clientData.authType === AuthType.CustomerIdSignature && (
                <>
                  <p><strong>Customer ID:</strong> {clientData.customerId}</p>
                </>
              )}

              {clientData.authType === AuthType.ApiKeyKeypair && (
                <>
                  <p><strong>API Key:</strong> ••••••••</p>
                  <p><strong>Key Pair:</strong> {clientData.keypairGenerated ? 'Generated' : 'Pending'}</p>
                </>
              )}
            </div>
            <Alert variant="info" style={{ marginTop: '1rem' }}>
              Clicking "Activate Client" will finalize the setup and activate the client for use.
            </Alert>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Modal $isOpen>
      <ModalOverlay onClick={onClose} />
      <ModalContent style={{ maxWidth: '700px' }}>
        <ModalHeader>
          <ModalTitle>Create New Client</ModalTitle>
          <Button variant="outline" size="sm" onClick={onClose}>
            <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
          </Button>
        </ModalHeader>
        
        <ModalBody>
          {/* Step Indicator */}
          <StepIndicator>
            {steps.map((step, index) => (
              <StepContainer key={step.key}>
                <Step
                  $active={index === currentStep}
                  $completed={completedSteps.includes(index)}
                  $error={errorStep === index}
                >
                  {completedSteps.includes(index) ? (
                    <CheckIcon style={{ width: '1rem', height: '1rem' }} />
                  ) : errorStep === index ? (
                    <ExclamationTriangleIcon style={{ width: '1rem', height: '1rem' }} />
                  ) : (
                    index + 1
                  )}
                </Step>
                <StepLabel>{step.label}</StepLabel>
              </StepContainer>
            ))}
          </StepIndicator>
          
          {/* Error Alert */}
          {errorStep === currentStep && (
            <Alert variant="error" style={{ marginBottom: '1rem' }}>
              This step failed. Please check your input and try again.
            </Alert>
          )}
          
          {/* Step Content */}
          {renderStepContent()}
        </ModalBody>
        
        <ModalFooter>
          {currentStep > 0 ? (
            <Button variant="outline" onClick={handleBack} disabled={isLoading}>
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
          )}
          {errorStep === currentStep ? (
            <Button onClick={handleRetry} disabled={isLoading}>
              Retry Step {currentStep + 1}
            </Button>
          ) : (
            <Button 
              onClick={handleNext} 
              disabled={!canProceed() || isLoading}
            >
              {isLoading ? 'Processing...' : 
               currentStep === steps.length - 1 ? 'Activate Client' : 'Next Step'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
      
      <SecretDisplayModal
        isOpen={showSecretModal}
        secret={generatedSecret}
        title="Client Secret Generated"
        clientId={createdClientId}
        secretType="client-secret"
        onComplete={handleSecretComplete}
      />
    </Modal>
  );
}